// src/pages/ExpenseCategoryList.js
import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import ExpenseCategoryServices from "../../Services/QRCodeServices/ExpenseCategoryServices/ExpenseCategoryServices";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../CRUD/generalAdmin/DeleteModal";
import ExpenseCategoryAddEditModal from "./Components/ExpenseCategoryAddEditModal";

function ExpenseCategory() {
  const [categoriesList, setCategoriesList] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const toggleModal = () => setModal(!modal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: true,
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="hstack gap-2">
          <Button
            color="btn btn-soft-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <i className="ri-edit-line"></i>
          </Button>
          <Button
            color="btn btn-soft-danger"
            size="sm"
            onClick={() => handleDeleteClick(row)}
          >
            <i className="ri-delete-bin-line"></i>
          </Button>
        </div>
      ),
    },
  ];

  async function fetchExpenseCategories() {
    try {
      setLoading(true);
      const fetchedCategories = await ExpenseCategoryServices.getList(); // Hypothetical service call
      console.log("🚀 ~ fetchExpenseCategories ~ fetchedCategories:", fetchedCategories)
      setCategoriesList(fetchedCategories);
      setFilteredCategories(fetchedCategories);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching expense categories:", err);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchExpenseCategories();
  }, []);

  useEffect(() => {
    let filtered = categoriesList;

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((category) =>
        category.title?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setFilteredCategories(filtered);
  }, [searchTerm, categoriesList]);

  const handleEdit = (row) => {
    setIsEdit(true);
    setSelectedCategory(row);
    toggleModal();
  };

  const handleCreate = () => {
    setIsEdit(false);
    setSelectedCategory(null);
    toggleModal();
  };

  const handleModalSuccess = () => {
    fetchExpenseCategories();
    toggleModal();
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await ExpenseCategoryServices.delete(selectedDeleteItem.id); // Hypothetical delete method
        toast.success("Expense category deleted successfully");
        fetchExpenseCategories();
      } catch (error) {
        toast.error("Failed to delete expense category");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Expense Categories List"
            pageTitle="Expense Categories"
          />
          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <FormGroup className="col-lg-3">
                      <Input
                        type="text"
                        placeholder="Search by Title"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                    <div className="ms-auto">
                      <Button color="primary" onClick={handleCreate}>
                        + Create Category
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={filteredCategories}
                    customStyles={customStyles}
                    responsive
                    striped
                    highlightOnHover
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ExpenseCategoryAddEditModal
        isOpen={modal}
        toggle={toggleModal}
        isEdit={isEdit}
        selectedCategory={selectedCategory}
        onSuccess={handleModalSuccess}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
        selectedItem={selectedDeleteItem}
      />
    </React.Fragment>
  );
}

export default ExpenseCategory;
